/**
 * Hook to fetch portfolio sections from API.
 * Uses a module-level singleton so all callers share ONE fetch — no re-fetch
 * on SPA navigation (data is stable until the admin signals "portfolio_updated").
 */

import { useState, useEffect, useMemo } from "react";
import { parsePortfolioUrl } from "@/config/portfolioConfig";
import { isCloudinaryImage } from "@/lib/cloudinaryUtils";
import type { PortfolioSection } from "@/config/portfolioConfig";

interface PortfolioStore {
  sections: PortfolioSection[];
  portfolioPublicEnabled: boolean;
  isLoading: boolean;
  refCount: number;
  fetched: boolean;
  listeners: Set<() => void>;
}

const store: PortfolioStore = {
  sections: [],
  portfolioPublicEnabled: true,
  isLoading: true,
  refCount: 0,
  fetched: false,
  listeners: new Set(),
};

function notify() {
  store.listeners.forEach((fn) => fn());
}

async function fetchPortfolio(soft = false) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch("/api/portfolio", { cache: "no-store", signal: controller.signal });
    clearTimeout(timeoutId);
    if (!res.ok) {
      if (!soft) { store.isLoading = false; notify(); }
      return;
    }
    const data = await res.json() as { portfolioPublicEnabled?: boolean; sections?: unknown[] };
    const pub = data.portfolioPublicEnabled !== false;
    store.portfolioPublicEnabled = pub;
    if (!pub) {
      store.sections = [];
    } else {
      const raw = (data.sections ?? []) as { id?: string; title?: string; urls?: unknown }[];
      store.sections = raw.map((s) => ({
        id: s.id,
        title: s.title ?? "Sección",
        urls: Array.isArray(s.urls)
          ? (s.urls as unknown[]).filter((u): u is string => typeof u === "string" && u.trim().length > 0)
          : [],
      }));
    }
    store.fetched = true;
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name !== "AbortError") {
      console.warn("[usePortfolioSections] Fetch error:", err.message);
    }
    store.sections = [];
    store.fetched = true;
  } finally {
    if (!soft) { store.isLoading = false; notify(); }
    else { notify(); }
  }
}

let storageHandler: ((e: StorageEvent) => void) | null = null;
let localUpdateHandler: (() => void) | null = null;
let visibilityHandler: (() => void) | null = null;

export function usePortfolioSections(forceRefresh = false) {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate((n) => n + 1);
    store.listeners.add(listener);
    store.refCount++;

    if (store.refCount === 1) {
      // First subscriber: start the singleton fetch + signals.
      if (!store.fetched) void fetchPortfolio();

      // Cross-tab: admin guardó en otra tab. storage event NO dispara en
      // la tab que escribió (esa usa el CustomEvent abajo).
      storageHandler = (e: StorageEvent) => {
        if (e.key === "portfolio_updated") {
          store.isLoading = false;
          void fetchPortfolio(true);
        }
      };
      // Same-tab: admin guardó en esta misma pestaña (preview abierto en
      // window distinta del mismo browser — el storage event no dispara acá).
      localUpdateHandler = () => {
        store.isLoading = false;
        void fetchPortfolio(true);
      };
      // Volver al tab tras estar minimizado: revalidamos por si admin tocó
      // mientras estábamos en background (browser puede haber throttleado
      // listeners y el storage event no nos llegó).
      visibilityHandler = () => {
        if (document.visibilityState === "visible") {
          void fetchPortfolio(true);
        }
      };
      window.addEventListener("storage", storageHandler);
      window.addEventListener("portfolio-updated", localUpdateHandler);
      document.addEventListener("visibilitychange", visibilityHandler);
    }

    if (forceRefresh) {
      store.fetched = false;
      store.isLoading = true;
      void fetchPortfolio();
    }

    return () => {
      store.listeners.delete(listener);
      store.refCount--;
      if (store.refCount === 0) {
        if (storageHandler) {
          window.removeEventListener("storage", storageHandler);
          storageHandler = null;
        }
        if (localUpdateHandler) {
          window.removeEventListener("portfolio-updated", localUpdateHandler);
          localUpdateHandler = null;
        }
        if (visibilityHandler) {
          document.removeEventListener("visibilitychange", visibilityHandler);
          visibilityHandler = null;
        }
      }
    };
  }, [forceRefresh]);

  const allImageUrls = useMemo(
    () =>
      store.sections
        .flatMap((s) => s.urls)
        .map((raw) => parsePortfolioUrl(raw?.trim() ?? ""))
        .filter((e) => e.url && !e.wide && isCloudinaryImage(e.url))
        .map((e) => e.url),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store.sections],
  );

  const showPortfolioNavLink = useMemo(() => {
    if (store.isLoading) return false;
    return store.sections.some((s) =>
      s.urls.some((u) => {
        const p = parsePortfolioUrl(u.trim());
        return Boolean(p.url && isCloudinaryImage(p.url));
      }),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.isLoading, store.sections]);

  return {
    sections: store.sections,
    isLoading: store.isLoading,
    allImageUrls,
    showPortfolioNavLink,
    portfolioPublicEnabled: store.portfolioPublicEnabled,
  };
}
